/**
 * Money (IMPLEMENTATION_PLAN.md §2.6): an integer amount in the currency's minor unit plus
 * an ISO 4217 code. Never floats, never major units, never a bare number in a money column.
 */
export interface Money {
  readonly amountMinor: number;
  /** Upper-case ISO 4217 code, e.g. "USD" or "EUR". */
  readonly currency: string;
}

/** Currencies whose minor unit is not 1/100 of the major unit (Intl needs the exponent). */
const MINOR_UNIT_DIGITS: Readonly<Record<string, number>> = {
  BHD: 3,
  CLP: 0,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KRW: 0,
  KWD: 3,
  OMR: 3,
  TND: 3,
  VND: 0,
};

export function minorUnitDigits(currency: string): number {
  return MINOR_UNIT_DIGITS[currency.toUpperCase()] ?? 2;
}

export function money(amountMinor: number, currency: string): Money {
  if (!Number.isSafeInteger(amountMinor)) {
    throw new RangeError(`amountMinor must be a safe integer, got ${amountMinor}`);
  }
  const code = currency.toUpperCase();
  if (!/^[A-Z]{3}$/.test(code)) {
    throw new RangeError(`currency must be a three-letter ISO 4217 code, got "${currency}"`);
  }
  return { amountMinor, currency: code };
}

function assertSameCurrency(a: Money, b: Money): void {
  if (a.currency !== b.currency) {
    throw new RangeError(`currency mismatch: ${a.currency} vs ${b.currency}`);
  }
}

export function addMoney(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return money(a.amountMinor + b.amountMinor, a.currency);
}

export function subtractMoney(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return money(a.amountMinor - b.amountMinor, a.currency);
}

export function multiplyMoney(a: Money, factor: number): Money {
  if (!Number.isSafeInteger(factor)) {
    throw new RangeError(`factor must be a safe integer, got ${factor}`);
  }
  return money(a.amountMinor * factor, a.currency);
}

/**
 * Multiplies by a real ratio, rounding half away from zero. Budget maths works in ratios
 * (pace shares, a 40 % step, a 20 % shift), and the result has to land back on a whole minor
 * unit: no platform accepts a fraction of a cent.
 */
export function scaleMoney(a: Money, ratio: number): Money {
  if (!Number.isFinite(ratio)) {
    throw new RangeError(`ratio must be finite, got ${ratio}`);
  }
  const scaled = a.amountMinor * ratio;
  return money(Math.sign(scaled) * Math.round(Math.abs(scaled)), a.currency);
}

/** Rounds towards zero, so splitting a budget never hands out more than there is. */
export function divideMoney(a: Money, divisor: number): Money {
  if (!Number.isFinite(divisor) || divisor === 0) {
    throw new RangeError(`divisor must be a non-zero finite number, got ${divisor}`);
  }
  return money(Math.trunc(a.amountMinor / divisor), a.currency);
}

/** `a` relative to `b`, as a plain number. Both must be the same currency. */
export function ratioOfMoney(a: Money, b: Money): number {
  assertSameCurrency(a, b);
  return a.amountMinor / b.amountMinor;
}

export function compareMoney(a: Money, b: Money): number {
  assertSameCurrency(a, b);
  return a.amountMinor - b.amountMinor;
}

export function maxMoney(a: Money, b: Money): Money {
  return compareMoney(a, b) >= 0 ? a : b;
}

export function minMoney(a: Money, b: Money): Money {
  return compareMoney(a, b) <= 0 ? a : b;
}

export function isZeroMoney(a: Money): boolean {
  return a.amountMinor === 0;
}

export function isNegativeMoney(a: Money): boolean {
  return a.amountMinor < 0;
}

/** Sums a list that may be empty, which is why the currency has to be given explicitly. */
export function sumMoney(amounts: readonly Money[], currency: string): Money {
  return amounts.reduce((total, next) => addMoney(total, next), money(0, currency));
}

/**
 * Renders for people with an explicit locale (decision 9: never the runtime default).
 * Minor units are converted with the currency's exponent, so 49900 USD → "$499.00".
 */
export function formatMoney(a: Money, locale: string): string {
  const digits = minorUnitDigits(a.currency);
  const major = a.amountMinor / 10 ** digits;
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: a.currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(major);
}
